import { Link, useSearchParams } from "@remix-run/react";
import { FiltersPagination } from "~/sanity/types-mapped";
import { setSearchParamsString } from "~/utils";
import * as styles from "./Pagination.css";
import { ButtonLink } from "./ButtonLink";
// @ts-ignore
import ChevronLeft from "@material-design-icons/svg/filled/chevron_left.svg?react";
// @ts-ignore
import ChevronRight from "@material-design-icons/svg/filled/chevron_right.svg?react";
import { Paragraph } from "./Paragraph";

export type PaginationProps = {
  pagination: FiltersPagination;
  anchorId?: string;
};

// @link https://www.jacobparis.com/content/remix-pagination
export const Pagination = ({ pagination, anchorId }: PaginationProps) => {
  const [searchParams] = useSearchParams();

  if (typeof pagination.pageNumber !== "number") {
    throw new Error("pageNumber must be typeof number");
  }

  const pageCount = Math.ceil(pagination.totalPageCount);

  const pagePrevious =
    pagination.pageNumber > 1 ? pagination.pageNumber - 1 : undefined;
  const pageNext =
    pagination.pageNumber < pageCount ? pagination.pageNumber + 1 : undefined;

  let visiblePagesDesktop: (number | string)[] = [];

  if (pageCount <= 5) {
    visiblePagesDesktop = Array.from({ length: pageCount }, (_, i) => i + 1);
  } else if (pagination.pageNumber <= 3) {
    visiblePagesDesktop = [1, 2, 3, "...", pageCount];
  } else if (pagination.pageNumber === 4) {
    visiblePagesDesktop = [1, "...", 4, "...", pageCount];
  } else if (pagination.pageNumber >= pageCount - 2) {
    visiblePagesDesktop = [1, "...", pageCount - 2, pageCount - 1, pageCount];
  } else {
    visiblePagesDesktop = [1, "...", pagination.pageNumber, "...", pageCount];
  }

  // console.log(visiblePagesDesktop, pagination.totalPageCount, pageCount);

  const PageNumber = ({
    isActive,
    pageNumber,
  }: {
    isActive: boolean;
    pageNumber: number;
  }) => {
    return (
      <Link
        className={styles.number({
          active: isActive,
        })}
        to={{
          search: setSearchParamsString(searchParams, {
            page: pageNumber,
          }),
          hash: anchorId || undefined,
        }}
      >
        <div className={styles.numberChild}>
          <Paragraph size="md">{pageNumber}</Paragraph>
        </div>
      </Link>
    );
  };

  return (
    <div className={styles.pagination}>
      <ButtonLink
        theme="dark"
        size="sm"
        isDisabled={!pagePrevious}
        link={{
          _type: "link",
          to: {
            search: setSearchParamsString(searchParams, {
              page: pagePrevious === 1 ? undefined : pagePrevious,
            }),
            hash: anchorId || undefined,
          },
        }}
        icons={{ iconPrimary: <ChevronLeft /> }}
      />

      <div className={styles.hidden({ isMobile: false })}>
        {visiblePagesDesktop.map((page, index) =>
          typeof page === "number" ? (
            <PageNumber
              key={index}
              isActive={pagination.pageNumber === page && pageCount > 1}
              pageNumber={page}
            />
          ) : (
            <div
              key={index}
              className={styles.number({
                active: false,
              })}
              style={{ pointerEvents: "none" }}
            >
              <div className={styles.numberChild}>
                <Paragraph size="md">{page}</Paragraph>
              </div>
            </div>
          )
        )}
      </div>
      <div className={styles.hidden({ isMobile: true })}>
        <PageNumber isActive={false} pageNumber={pagination.pageNumber} />
      </div>

      <ButtonLink
        theme="dark"
        size="sm"
        isDisabled={!pageNext}
        link={{
          _type: "link",
          to: {
            search: setSearchParamsString(searchParams, {
              page: pageNext,
            }),
            hash: anchorId || undefined,
          },
        }}
        icons={{ iconPrimary: <ChevronRight /> }}
      />
    </div>
  );
};
